import { observable, action, computed } from 'mobx'
import {
  ModalComponentKeyTypes, RootStore,
  EDITOR_MONITOR_STORE,
  EDITOR_TIMELINE_STORE, GENERAL_STORE, EDITOR_SHOTS_STORE,
  EDITOR_INTERACTIVE_AREAS_STORE
} from 'store'
import { isRightBttnClicked, getCursorPosition, actionTypes } from './utils'
import {
  defaultContextMenu, defaultMarkAsOptions,
  selectLabels, defaultCategory02, jumpToLabels, copyLabels,
  cat04Labels, defaultCategory05, cat05labels, defaultCategory06, zIndexLabels
} from './default-ia-context-menu'
import { Rect, InteractiveArea, InteractiveAreaAPI, VideoEventTypes, EntityClassTypes, VideoEntityEvent, IaAction, IaActionTypes } from 'models'
import { EDITOR_ACTION_HISTORY_STORE } from 'store/EditorActionHistoryStore'

export const EDITOR_CONTEXT_MENU_STORE = 'EDITOR_CONTEXT_MENU_STORE'

export class EditorContextMenuStore {
  @observable contextMenu: any = null;
  @observable copiedIaRects: Rect[] = [];

  rootStore: RootStore;

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @computed get copiedRectsIaDetails () {
    return this.copiedIaRects
      .map(r => this.rootStore[EDITOR_INTERACTIVE_AREAS_STORE]
        .getInteractiveAreaById(r.interactiveAreaId)
      )
  }

  @computed get isPasteOptionAvailable () {
    if (!this.copiedIaRects.length || !this.copiedRectsIaDetails.length) return false

    let result = false

    for (const ia of this.copiedRectsIaDetails) {
      if (
        !ia.shots.includes(this.rootStore[EDITOR_TIMELINE_STORE].currentMonitorShotId)
      ) {
        result = true
        break
      }
    }

    return result
  }

  onStageRightClick = data => {
    this.rootStore[EDITOR_TIMELINE_STORE].stop()
    this.deselectAllIas()
    this.setContextMenu(data)
  }

  onRectangleSelect = (data, iaId) => {
    const {
      [EDITOR_TIMELINE_STORE]: { stop },
      [EDITOR_INTERACTIVE_AREAS_STORE]: { setSelectedInteractiveAreaIds }
    } = this.rootStore

    stop()

    isRightBttnClicked(data)
      ? this.setContextMenu(data, iaId)
      : setSelectedInteractiveAreaIds([iaId])
  }

  @action.bound setContextMenu (data, iaId = null) {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: { isIaSelected, setSelectedInteractiveAreaIds }
    } = this.rootStore
    if (iaId && !isIaSelected(iaId)) {
      setSelectedInteractiveAreaIds([iaId])
    }

    this.contextMenu = {
      ...this.generateContextMenu(),
      position: getCursorPosition(data)
    }
  }

  deselectAllIas = () => {
    this.rootStore[EDITOR_INTERACTIVE_AREAS_STORE].setSelectedInteractiveAreaIds()
  }

  generateContextMenu = () => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        selectedIAsNumber,
        selectedInteractiveAreaIds,
        selectedInteractiveAreas
      },
      [EDITOR_MONITOR_STORE]: { rectanglesForCurrentFrame },
      [EDITOR_TIMELINE_STORE]: { currentMonitorGroupName }
    } = this.rootStore

    const currentRectContextMenu: any = { ...defaultContextMenu }
    const areIASelected = selectedIAsNumber > 0

    // category 01
    const generateCategory01 = () => {
      const isSingleIASelected = selectedIAsNumber === 1
      const category01 = []
      let isMarkAsOptionAvailable = true

      if (isSingleIASelected) {
        const { eventAtCurrentFrame } = rectanglesForCurrentFrame
          .find(r => r.interactiveAreaId === selectedInteractiveAreaIds[0])

        const labelTypes = {
          Anchor: 'Anchor',
          MarkIn: 'Mark In',
          MarkOut: 'Mark Out'
        }
        const getLabel = () => {
          if (!eventAtCurrentFrame) return labelTypes.Anchor

          switch (eventAtCurrentFrame.type) {
            case VideoEventTypes.IN: return labelTypes.MarkIn
            case VideoEventTypes.OUT: return labelTypes.MarkOut
            default: return labelTypes.Anchor
          }
        }
        const label = getLabel()
        isMarkAsOptionAvailable = label === labelTypes.Anchor

        const getAction = () => {
          if (!isMarkAsOptionAvailable) return null

          const actionType = eventAtCurrentFrame
            ? actionTypes.deleteEvent
            : actionTypes.changeRectangle

          return () => this.handleContextMenuAction(actionType)
        }

        category01.push({
          label,
          action: getAction(),
          checkable: true,
          checked: !!eventAtCurrentFrame,
          shortcut: '',
          subOptions: []
        })
      }

      const generateMarkAsOptions = () => {
        const markAsOptions = { ...defaultMarkAsOptions }

        !isSingleIASelected && (markAsOptions.label = `Mark ${selectedIAsNumber} IAs as`)

        markAsOptions.subOptions[0].action =
          () => this.handleContextMenuAction(actionTypes.markAsIn)
        markAsOptions.subOptions[1].action =
          () => this.handleContextMenuAction(actionTypes.markAsOut)

        return markAsOptions
      }
      isMarkAsOptionAvailable && category01.push(generateMarkAsOptions())

      return category01
    }
    if (areIASelected) {
      currentRectContextMenu.options.category01 = generateCategory01()
    } else {
      delete currentRectContextMenu.options.category01
    }

    // category 02
    const generateCategory02 = () => {
      const category02 = [...defaultCategory02]

      category02[0].subOptions.forEach(option => {
        option.action = () => this.handleContextMenuAction(option.label)
      })

      return category02
    }
    if (rectanglesForCurrentFrame.length) {
      currentRectContextMenu.options.category02 = generateCategory02()
    } else {
      delete currentRectContextMenu.options.category02
    }

    // category 03
    currentRectContextMenu.options.category03[0].action =
      () => this.handleContextMenuAction(actionTypes.createNewCut)
    currentRectContextMenu.options.category03[1].subOptions
      .forEach(option => {
        option.action = () => this.handleContextMenuAction(option.label)
      })

    // category 04
    const pasteOption = {
      label: copyLabels.paste,
      action: () => this.handleContextMenuAction(copyLabels.paste),
      checkable: false,
      checked: false,
      shortcut: 'ctrl+v',
      subOptions: []
    }
    const generateCategory04 = () => {
      const category04 = []
      const isCurrentShotInAGroup = !!currentMonitorGroupName
      if (isCurrentShotInAGroup) {
        category04.push({
          label: copyLabels.iasToShotsInGroup,
          action: () => this.handleContextMenuAction(copyLabels.iasToShotsInGroup),
          checkable: false,
          checked: false,
          shortcut: 'ctrl+j',
          subOptions: []
        })
      }

      category04.push({
        label: copyLabels.copy,
        action: () => this.handleContextMenuAction(copyLabels.copy),
        checkable: false,
        checked: false,
        shortcut: 'ctrl+c',
        subOptions: []
      })

      if (this.isPasteOptionAvailable) {
        category04.push(pasteOption)
      }

      const isEntityAttachedToAnySelectedArea =
        !!selectedInteractiveAreas.filter(ia => ia.entityId).length
      if (isEntityAttachedToAnySelectedArea) {
        category04.push({
          label: cat04Labels.detachEntity,
          action: () => this.handleContextMenuAction(cat04Labels.detachEntity),
          checkable: false,
          checked: false,
          subOptions: [
            {
              label: cat04Labels.deleteIaFromShot,
              action: () => console.log('Still in progress'),
              checkable: false,
              checked: false,
              subOptions: []
            },
            {
              label: cat04Labels.deleteIaFromAllShots,
              action: () => this.handleContextMenuAction(cat04Labels.detachEntity),
              checkable: false,
              checked: false,
              subOptions: []
            }
          ]
        })
      }

      category04.push({
        label: 'Delete Interactive Area',
        action: null,
        checkable: false,
        checked: false,
        shortcut: '',
        subOptions: [
          {
            label: cat04Labels.deleteIaFromShot,
            action: () => this.handleContextMenuAction(cat04Labels.deleteIaFromShot),
            checkable: false,
            checked: false,
            shortcut: 'del',
            subOptions: []
          },
          {
            label: cat04Labels.deleteIaFromAllShots,
            action: () => this.handleContextMenuAction(cat04Labels.deleteIaFromAllShots),
            checkable: false,
            checked: false,
            shortcut: 'ctrl+shift+del',
            subOptions: []
          }
        ]
      })

      return category04
    }
    if (areIASelected) {
      currentRectContextMenu.options.category04 = generateCategory04()
    } else if (this.isPasteOptionAvailable) {
      currentRectContextMenu.options.category04 = [pasteOption]
    } else {
      delete currentRectContextMenu.options.category04
    }

    // category 05
    currentRectContextMenu.options.category05 = defaultCategory05
    currentRectContextMenu.options.category05[0].action =
      () => this.handleContextMenuAction(cat05labels.setLocation)

    // category 06 - Z-Order
    const generateCategory06 = () => {
      const category06 = [...defaultCategory06]

      category06[0].subOptions.forEach(option => {
        option.action = () => this.handleContextMenuAction(option.label)
      })

      return category06
    }
    if (areIASelected) {
      currentRectContextMenu.options.category06 = generateCategory06()
    } else {
      delete currentRectContextMenu.options.category06
    }

    return currentRectContextMenu
  }

  markPointAs = type => {
    const {
      [EDITOR_MONITOR_STORE]: {
        rectanglesForCurrentFrame,
        markAs
      },
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        selectedInteractiveAreaIds,
        selectedIAsNumber
      }
    } = this.rootStore

    if (!selectedIAsNumber || !rectanglesForCurrentFrame.length) return

    selectedInteractiveAreaIds
      .forEach(iaId => {
        const {
          eventAtCurrentFrame,
          x, y, width, height
        } = rectanglesForCurrentFrame.find(r => r.interactiveAreaId === iaId)

        const eventType = type === actionTypes.markAsIn
          ? VideoEventTypes.IN
          : VideoEventTypes.OUT;

        (!eventAtCurrentFrame || !eventAtCurrentFrame.type) &&
          markAs(eventType, { x, y, width, height }, iaId)
      })
  }

  handleContextMenuAction = type => {
    this.clearContextMenu()
    const {
      [EDITOR_MONITOR_STORE]: { rectanglesForCurrentFrame, changeRectangle },
      [EDITOR_INTERACTIVE_AREAS_STORE]: { deleteEvent, selectedInteractiveAreaIds }
    } = this.rootStore

    switch (type) {
      case actionTypes.markAsIn:
      case actionTypes.markAsOut:
        this.markPointAs(type)
        break

      case actionTypes.deleteEvent:
        selectedInteractiveAreaIds
          .forEach(iaId => {
            const {
              eventAtCurrentFrame
            } = rectanglesForCurrentFrame.find(r => r.interactiveAreaId === iaId);

            (eventAtCurrentFrame && !eventAtCurrentFrame.type) &&
              deleteEvent(eventAtCurrentFrame)
          })
        break

      case actionTypes.changeRectangle:
        selectedInteractiveAreaIds
          .forEach(iaId => {
            const {
              eventAtCurrentFrame,
              x, y, width, height
            } = rectanglesForCurrentFrame.find(r => r.interactiveAreaId === iaId);
            (!eventAtCurrentFrame || !eventAtCurrentFrame.type) &&
              changeRectangle({ x, y, width, height }, iaId)
          })
        break

      case selectLabels.all:
        this.selectAllIAs()
        break

      case selectLabels.notEmptyIas:
        this.selectNotEmptyIas()
        break

      case selectLabels.productIas:
        this.selectProductIas()
        break

      case selectLabels.infoIas:
        this.selectInfoIas()
        break

      case selectLabels.emptyIas:
        this.selectEmptyIas()
        break

      case actionTypes.createNewCut: {
        const {
          [EDITOR_TIMELINE_STORE]: {
            currentMonitorShotId: id,
            currentMonitorFrame
          },
          [EDITOR_SHOTS_STORE]: { splitShot }
        } = this.rootStore
        splitShot(id, currentMonitorFrame)
        break
      }

      case jumpToLabels.nextShotStart:
        this.jumpToNextShotStart()
        break

      case jumpToLabels.prevShotStart:
        this.jumpToPreviousShotStart()
        break

      case copyLabels.iasToShotsInGroup:
        this.copyIasToShotsInGroup()
        break

      case copyLabels.copy:
        this.copyIas()
        break

      case copyLabels.paste:
        this.pasteIas()
        break

      case cat04Labels.detachEntity:
        this.detachEntityFromIas()
        break

      case cat04Labels.deleteIaFromAllShots:
        this.deleteSelectedIas()
        break

      case cat04Labels.deleteIaFromShot:
        this.deleteSelectedIasFromCurrentShot()
        break

      case cat05labels.setLocation:
        this.handleSetLocation()
        break

      case zIndexLabels.moveToTop:
        this.moveIasToTop()
        break

      case zIndexLabels.moveToBottom:
        this.moveIasToBottom()
        break

      case zIndexLabels.moveUp:
        this.moveIasUp()
        break

      case zIndexLabels.moveDown:
        this.moveIasDown()
        break
    }
  }

  @action.bound clearContextMenu () {
    this.contextMenu = null
  }

  // Select context menu actions
  selectIasByEntityClass = (
    filterCondition: (entityClass: EntityClassTypes) => boolean
  ) => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        getInteractiveAreaById,
        currentFrameInteractiveAreaIds,
        setSelectedInteractiveAreaIds
      }
    } = this.rootStore

    const selectedIds = currentFrameInteractiveAreaIds
      .filter(iaId => filterCondition(getInteractiveAreaById(iaId).entityClass))

    setSelectedInteractiveAreaIds(selectedIds)
  }

  selectAllIAs = () => {
    const filterCondition = () => true
    this.selectIasByEntityClass(filterCondition)
  }

  selectNotEmptyIas = () => {
    const filterCondition = entityClass => entityClass !== null
    this.selectIasByEntityClass(filterCondition)
  }

  selectProductIas = () => {
    const filterCondition = entityClass => entityClass === EntityClassTypes.PRODUCT
    this.selectIasByEntityClass(filterCondition)
  }

  selectInfoIas = () => {
    const filterCondition = entityClass => entityClass === EntityClassTypes.PERSON
    this.selectIasByEntityClass(filterCondition)
  }

  selectEmptyIas = () => {
    const filterCondition = entityClass => entityClass === null
    this.selectIasByEntityClass(filterCondition)
  }

  // Jump to action
  jumpToNextShotStart = () => {
    const {
      [EDITOR_SHOTS_STORE]: { shotsOfSelectedGroups },
      [EDITOR_TIMELINE_STORE]: { currentMonitorShot, setCurrentMonitorFrame }
    } = this.rootStore

    const currentShotIndex = shotsOfSelectedGroups
      .findIndex(shot => shot.id === currentMonitorShot.id)
    const nextShotIndex = currentShotIndex < shotsOfSelectedGroups.length - 1
      ? currentShotIndex + 1
      : 0
    setCurrentMonitorFrame(shotsOfSelectedGroups[nextShotIndex].startFrame)
  }

  jumpToPreviousShotStart = () => {
    const {
      [EDITOR_SHOTS_STORE]: { shotsOfSelectedGroups },
      [EDITOR_TIMELINE_STORE]: { currentMonitorShot, setCurrentMonitorFrame }
    } = this.rootStore

    const currentShotIndex = shotsOfSelectedGroups
      .findIndex(shot => shot.id === currentMonitorShot.id)
    const prevShotIndex = currentShotIndex > 0
      ? currentShotIndex - 1
      : shotsOfSelectedGroups.length - 1
    setCurrentMonitorFrame(shotsOfSelectedGroups[prevShotIndex].startFrame)
  }

  jumpToNextInteractiveArea = () => {
    const {
      [EDITOR_TIMELINE_STORE]: {
        currentMonitorFrame,
        setCurrentMonitorFrame,
        timelineFrames
      },
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        currentFrameInteractiveAreaIds,
        sortedEventsList
      }
    } = this.rootStore

    const nextIaInEvent = sortedEventsList
      .find(e =>
        e.t > currentMonitorFrame &&
        timelineFrames.includes(e.t) &&
        e.type === 'I' &&
        !currentFrameInteractiveAreaIds.includes(e.interactiveAreaId)
      )
    if (nextIaInEvent) setCurrentMonitorFrame(nextIaInEvent.t)
  }

  jumpToPreviousInteractiveArea = () => {
    const {
      [EDITOR_TIMELINE_STORE]: {
        currentMonitorFrame,
        setCurrentMonitorFrame,
        timelineFrames
      },
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        currentFrameInteractiveAreaIds,
        sortedEventsList
      }
    } = this.rootStore
    const prevIaInEvent = sortedEventsList
      .find(e =>
        e.t < currentMonitorFrame &&
        timelineFrames.includes(e.t) &&
        e.type === 'I' &&
        !currentFrameInteractiveAreaIds.includes(e.interactiveAreaId)
      )
    if (prevIaInEvent) setCurrentMonitorFrame(prevIaInEvent.t)
  }

  // Copy actions
  copyIasToShotsInGroup = () => {
    const {
      [EDITOR_TIMELINE_STORE]: { currentMonitorGroupShots, currentMonitorFrame },
      [EDITOR_MONITOR_STORE]: { rectanglesForCurrentFrame, descaleRectDetails },
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        saveIas,
        selectedInteractiveAreas,
        selectedIAsNumber
      }
    } = this.rootStore

    if (!currentMonitorFrame || !selectedIAsNumber) return

    const selectedIas = selectedInteractiveAreas.map(this.parseToApiIa)

    const iasToUpdate = []
    selectedIas.forEach(ia => {
      const shotsToAddToIa = currentMonitorGroupShots
        .filter(shot => {
          const isShotAlreadyAdded = !!ia.shots.find(s => s.id === shot.id)
          if (isShotAlreadyAdded) return false

          return shot.startFrame >= currentMonitorFrame
        })

      const shouldIaBeUpdated = !!shotsToAddToIa.length
      if (!shouldIaBeUpdated) return

      const eventsToAddToIa: VideoEntityEvent[] = []
      const { x, y, width, height } = rectanglesForCurrentFrame
        .find(rect => rect.interactiveAreaId === ia.id)
      const r = descaleRectDetails({ x, y, width, height })
      shotsToAddToIa.forEach(shot => {
        eventsToAddToIa.push({
          eventId: null,
          interactiveAreaId: ia.id,
          id: null,
          t: shot.startFrame,
          r,
          type: VideoEventTypes.IN
        })
        eventsToAddToIa.push({
          eventId: null,
          interactiveAreaId: ia.id,
          id: null,
          t: shot.endFrame,
          r,
          type: VideoEventTypes.OUT
        })
      })

      ia.shots = [...ia.shots, ...shotsToAddToIa]
      ia.events = [...ia.events, ...eventsToAddToIa]

      iasToUpdate.push({ ia })
    })

    saveIas(iasToUpdate)
  }

  @action.bound copyIas () {
    this.copiedIaRects = this.rootStore[EDITOR_MONITOR_STORE].selectedCurrentRectangles
  }

  pasteIas = () => {
    const {
      [EDITOR_TIMELINE_STORE]: {
        currentMonitorShotId,
        currentMonitorShot,
        currentMonitorFrame
      },
      [EDITOR_MONITOR_STORE]: { descaleRectDetails },
      [EDITOR_INTERACTIVE_AREAS_STORE]: { saveIas },
      [EDITOR_ACTION_HISTORY_STORE]: { saveIaAction }
    } = this.rootStore

    const payload = this.copiedRectsIaDetails
      .filter(ia => !ia.shots.includes(currentMonitorShotId))
      .map(this.parseToApiIa)
      .map(beforeVersion => {
        const action = new IaAction(IaActionTypes.PASTE, { ...beforeVersion })
        saveIaAction(action)

        const shots = [...beforeVersion.shots, currentMonitorShot]
        const events: VideoEntityEvent[] = [...beforeVersion.events]
        const { x, y, width, height } = this.copiedIaRects
          .find(rect => rect.interactiveAreaId === beforeVersion.id)
        const r = descaleRectDetails({ x, y, width, height })
        events.push({
          eventId: null,
          interactiveAreaId: beforeVersion.id,
          id: null,
          t: currentMonitorFrame,
          r,
          type: VideoEventTypes.IN
        })
        events.push({
          eventId: null,
          interactiveAreaId: beforeVersion.id,
          id: null,
          t: currentMonitorShot.endFrame,
          r,
          type: VideoEventTypes.OUT
        })
        const ia = { ...beforeVersion, shots, events } as InteractiveAreaAPI
        action.setAfterVersion({ ...ia }, true)
        return { ia, action }
      })

    saveIas(payload)
  }

  // other category 04 options
  detachEntityFromIas = () => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        selectedInteractiveAreas, saveIas
      },
      [EDITOR_ACTION_HISTORY_STORE]: { saveIaAction }
    } = this.rootStore

    const payload = selectedInteractiveAreas
      .filter(ia => ia.entityId)
      .map(this.parseToApiIa)
      .map(beforeVersion => {
        const action = new IaAction(IaActionTypes.DETACH_ENTITY, beforeVersion)
        saveIaAction(action)
        const ia = {
          ...beforeVersion,
          entityId: null,
          entityClass: null
        } as InteractiveAreaAPI
        action.setAfterVersion(ia, true)
        return { ia, action }
      })

    saveIas(payload)
  }

  deleteSelectedIas = () => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        selectedInteractiveAreaIds,
        setSelectedInteractiveAreaIds,
        selectedIAsNumber
      },
      [EDITOR_MONITOR_STORE]: { deleteIas },
      [EDITOR_TIMELINE_STORE]: { currentMonitorShot }
    } = this.rootStore

    if (!currentMonitorShot || !selectedIAsNumber) return

    deleteIas([...selectedInteractiveAreaIds])
    setSelectedInteractiveAreaIds()
  }

  deleteSelectedIasFromCurrentShot = () => {
    const {
      [EDITOR_TIMELINE_STORE]: {
        currentMonitorShot
      },
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        saveIas,
        selectedInteractiveAreas,
        selectedIAsNumber
      },
      [EDITOR_ACTION_HISTORY_STORE]: {
        saveIaAction
      }
    } = this.rootStore

    if (!currentMonitorShot || !selectedIAsNumber) return

    const { id, startFrame, endFrame } = currentMonitorShot
    const payload = selectedInteractiveAreas
      .map(this.parseToApiIa)
      .map(beforeVersion => {
        const shots = beforeVersion.shots.filter(shot => shot.id !== id)
        const events = beforeVersion.events
          .filter(({ t }) => t < startFrame || t > endFrame)

        const action = new IaAction(IaActionTypes.DELETE_FROM_SHOT, beforeVersion)
        saveIaAction(action)

        const ia = { ...beforeVersion, shots, events } as InteractiveAreaAPI
        return { ia, action }
      })

    saveIas(payload)
  }

  // category 05 actions
  handleSetLocation = () => {
    const {
      [EDITOR_TIMELINE_STORE]: { currentMonitorShot },
      [GENERAL_STORE]: { setDetailsAndShowModalDialog }
    } = this.rootStore

    if (!currentMonitorShot) return

    setDetailsAndShowModalDialog({
      title: 'Location',
      onSubmit: this.setLocation,
      componentKey: ModalComponentKeyTypes.LOCATION_INPUT
    })
  }

  setLocation = location => {
    const {
      [EDITOR_TIMELINE_STORE]: { currentMonitorShotLocation, currentMonitorShotId },
      [EDITOR_SHOTS_STORE]: { sortedShotIds, shots, updateShots, getShotById }
    } = this.rootStore

    if (
      (!location && !currentMonitorShotLocation) ||
      (location === currentMonitorShotLocation)
    ) return

    const index = sortedShotIds.findIndex(id => currentMonitorShotId.toString() === id)
    const shotIdsUpToCurrentShot = sortedShotIds
      .slice(0, index + 1)
      .reverse()

    const currentSetLocation = getShotById(currentMonitorShotId).location
    const shotsToUpdate = []

    for (let i = 0; i <= shotIdsUpToCurrentShot.length; i++) {
      const shot = { ...shots[shotIdsUpToCurrentShot[i]] }
      if (currentSetLocation !== shot.location) break
      shot.location = location
      shotsToUpdate.push(shot)
    }

    updateShots(shotsToUpdate)
  }

  // utils
  parseToApiIa = (ia: InteractiveArea): InteractiveAreaAPI => {
    const {
      [EDITOR_SHOTS_STORE]: { getShotById },
      [EDITOR_INTERACTIVE_AREAS_STORE]: { getEventById }
    } = this.rootStore

    const interactiveArea = {
      ...ia,
      shots: ia.shots.map(getShotById),
      events: ia.events.map(getEventById)
    }
    return interactiveArea
  }

  // Z-Order manipulation methods
  moveIasToTop = () => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        selectedInteractiveAreaIds
      }
    } = this.rootStore

    if (!selectedInteractiveAreaIds.length) return

    this.updateZOrder(selectedInteractiveAreaIds, 'top')
  }

  moveIasToBottom = () => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        selectedInteractiveAreaIds
      }
    } = this.rootStore

    if (!selectedInteractiveAreaIds.length) return

    this.updateZOrder(selectedInteractiveAreaIds, 'bottom')
  }

  moveIasUp = () => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        selectedInteractiveAreaIds
      }
    } = this.rootStore

    if (!selectedInteractiveAreaIds.length) return

    this.updateZOrder(selectedInteractiveAreaIds, 'up')
  }

  moveIasDown = () => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        selectedInteractiveAreaIds
      }
    } = this.rootStore

    if (!selectedInteractiveAreaIds.length) return

    this.updateZOrder(selectedInteractiveAreaIds, 'down')
  }

  updateZOrder = (iaIds: number[], direction: 'top' | 'bottom' | 'up' | 'down') => {
    const {
      [EDITOR_INTERACTIVE_AREAS_STORE]: {
        interactiveAreas,
        saveIas
      },
      [EDITOR_MONITOR_STORE]: { rectanglesForCurrentFrame },
      [EDITOR_ACTION_HISTORY_STORE]: { saveIaAction }
    } = this.rootStore

    // Only work with IAs that are visible in the current frame
    const currentFrameIaIds = rectanglesForCurrentFrame.map(r => r.interactiveAreaId)
    const visibleIas = currentFrameIaIds
      .map(id => interactiveAreas[id])
      .filter(ia => ia)

    if (visibleIas.length === 0) return

    // Sort by current z-order (index determines layer order, lower index = bottom, higher index = top)
    const sortedIas = visibleIas
      .map(ia => ({ ...ia }))
      .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))

    // Find the selected IAs indices in the sorted list
    const selectedIndices = iaIds
      .map(id => sortedIas.findIndex(ia => ia.id === id))
      .filter(idx => idx !== -1)
      .sort((a, b) => a - b)

    if (selectedIndices.length === 0) return

    let newSortedIas = [...sortedIas]

    switch (direction) {
      case 'top': {
        // Move selected IAs to the end (top layer)
        const selectedIas = selectedIndices.map(idx => newSortedIas[idx])
        newSortedIas = newSortedIas.filter((_, idx) => !selectedIndices.includes(idx))
        newSortedIas.push(...selectedIas)
        break
      }

      case 'bottom': {
        // Move selected IAs to the beginning (bottom layer)
        const selectedIasBottom = selectedIndices.map(idx => newSortedIas[idx])
        newSortedIas = newSortedIas.filter((_, idx) => !selectedIndices.includes(idx))
        newSortedIas.unshift(...selectedIasBottom)
        break
      }

      case 'up': {
        // Move each selected IA up by one position (increase z-order)
        // Process from highest index to lowest to avoid conflicts
        for (let i = selectedIndices.length - 1; i >= 0; i--) {
          const currentIdx = selectedIndices[i]
          if (currentIdx < newSortedIas.length - 1) {
            // Check if the next position is also selected
            const nextIdx = currentIdx + 1
            const isNextSelected = selectedIndices.includes(nextIdx)
            if (!isNextSelected) {
              // Swap with next element (higher z-order)
              const temp = newSortedIas[currentIdx]
              newSortedIas[currentIdx] = newSortedIas[nextIdx]
              newSortedIas[nextIdx] = temp
              // Update the selected index for subsequent iterations
              selectedIndices[i] = nextIdx
            }
          }
        }
        break
      }

      case 'down': {
        // Move each selected IA down by one position (decrease z-order)
        // Process from lowest index to highest to avoid conflicts
        for (let i = 0; i < selectedIndices.length; i++) {
          const currentIdx = selectedIndices[i]
          if (currentIdx > 0) {
            // Check if the previous position is also selected
            const prevIdx = currentIdx - 1
            const isPrevSelected = selectedIndices.includes(prevIdx)
            if (!isPrevSelected) {
              // Swap with previous element (lower z-order)
              const temp = newSortedIas[currentIdx]
              newSortedIas[currentIdx] = newSortedIas[prevIdx]
              newSortedIas[prevIdx] = temp
              // Update the selected index for subsequent iterations
              selectedIndices[i] = prevIdx
            }
          }
        }
        break
      }
    }

    // Reassign index values based on new order
    // Index determines z-order: 0 is bottom, higher numbers are on top
    const payload = newSortedIas.map((ia, position) => {
      const beforeVersion = this.parseToApiIa(ia)
      const action = new IaAction(IaActionTypes.CHANGE_Z_ORDER, beforeVersion)
      saveIaAction(action)

      // Assign new index based on position in the sorted array
      const updatedIa = this.parseToApiIa({ ...ia, zIndex: position })
      action.setAfterVersion(updatedIa, true)
      return { ia: updatedIa, action }
    })

    saveIas(payload)
  }
}
